import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldAlert, Download, ExternalLink } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2.5 border-b border-slate-200 last:border-0">
      <div className="text-xs uppercase tracking-wide text-slate-500 font-mono">{k}</div>
      <div className="col-span-2 text-sm text-slate-800">{v || "—"}</div>
    </div>
  );
}

export default function PublicVerify() {
  const { publicId } = useParams();
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/verify/${publicId}`).then(({ data }) => setRec(data))
      .catch(() => setErr("No issued EPD found for this identifier."))
      .finally(() => setLoading(false));
  }, [publicId]);

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-[#0f172a] text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-2.5">
          <BrandMark size={32} />
          <div>
            <div className="font-display font-semibold">EPD Nigeria</div>
            <div className="text-[11px] text-slate-400">Environmental Product Declaration Record</div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6 md:p-10">
        {loading ? <p className="text-sm text-slate-500">Verifying…</p> :
          err ? (
            <div data-testid="verify-notfound" className="bg-white border rounded-md p-8 text-center">
              <ShieldAlert className="h-8 w-8 text-red-500 mx-auto mb-3" />
              <p className="text-slate-700">{err}</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-300 rounded-sm shadow-sm">
              <div className="p-6 md:p-8 border-b border-slate-200 flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-mono">EPD Record</div>
                  <h1 className="font-display text-2xl font-semibold mt-1" data-testid="verify-product">{rec.product}</h1>
                  <div className="text-sm text-slate-600 mt-1">{rec.manufacturer}</div>
                  <div className="font-mono text-sm mt-2 text-slate-800" data-testid="verify-public-id">{rec.epd_public_id}</div>
                </div>
                <StatusBadge status={rec.status} testid="verify-status-badge" className="text-sm px-3 py-1" />
              </div>

              {rec.is_demo && (
                <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-xs text-amber-800 font-medium">
                  DEMO DATA — NOT FOR PUBLICATION
                </div>
              )}

              <div className={`px-6 md:px-8 py-4 border-b flex items-center gap-3 ${rec.independently_verified ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                {rec.independently_verified
                  ? <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  : <ShieldAlert className="h-5 w-5 text-slate-500" />}
                <div className="text-sm" data-testid="verify-verification-status">
                  <span className="font-medium">{rec.independently_verified ? "Independently Verified" : "Not independently verified"}</span>
                  <span className="text-slate-500"> — {rec.verification_status}</span>
                </div>
              </div>

              <div className="p-6 md:p-8">
                <Row k="Manufacturer" v={rec.manufacturer} />
                <Row k="Product" v={rec.product} />
                <Row k="Plant" v={rec.plant} />
                <Row k="Category" v={rec.category === "cement" ? "Cement" : "Ready-Mix Concrete"} />
                <Row k="EPD ID" v={<span className="font-mono">{rec.epd_public_id}</span>} />
                <Row k="Version" v={`V${rec.version}`} />
                <Row k="Issue date" v={rec.issue_date} />
                <Row k="Expiry date" v={rec.expiry_date} />
                <Row k="Declared unit" v={rec.declared_unit} />
                <Row k="GWP-total (A1–A3)" v={rec.gwp_total != null ? `${rec.gwp_total} kg CO₂ eq` : "—"} />
                <Row k="Standards" v={(rec.standards || []).join(", ")} />
                <Row k="PCR" v={`${rec.pcr || ""}${rec.pcr_version ? " · " + rec.pcr_version : ""}`} />
                <Row k="Reference period" v={rec.reference_period} />
                {rec.independently_verified && <>
                  <Row k="Independent verifier" v={rec.verifier_name} />
                  <Row k="Verifier organisation" v={rec.verifier_org} />
                  <Row k="Programme operator" v={rec.programme_operator} />
                  <Row k="Programme registration" v={rec.programme_registration} />
                </>}
              </div>

              <div className="px-6 md:px-8 pb-8 flex flex-wrap gap-3">
                <a href={`${API}/verify/${rec.epd_public_id}/pdf`} target="_blank" rel="noreferrer">
                  <Button data-testid="verify-view-pdf"><Download className="h-4 w-4 mr-1.5" /> View EPD (PDF)</Button>
                </a>
                {rec.status === "SUPERSEDED" && rec.current_public_id && (
                  <Link to={`/verify/${rec.current_public_id}`}>
                    <Button variant="outline" data-testid="verify-current-link">
                      <ExternalLink className="h-4 w-4 mr-1.5" /> View current version
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          )}
        <p className="text-center text-xs text-slate-400 mt-6">
          Published via EPD Nigeria — enterprise EPD infrastructure. EPD Nigeria provides the platform and automated technical validation; it is not itself an independent verifier or programme operator.
        </p>
      </div>
    </div>
  );
}
